import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useProvider } from "../contexts/ProviderContext";

const AcceptInvite: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const token = params.get("token") || "";
  const type = params.get("type") || "shop"; // "shop", "provider", or "global"
  const code = params.get("code");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshProviders, setCurrentProviderId } = useProvider();

  const [status, setStatus] = useState<"idle"|"working"|"done"|"error">("idle");
  const [message, setMessage] = useState<string>("");
  const hasRun = useRef(false);
  const codeExchanged = useRef(false);

  // Handle OAuth code exchange when returning from Google login
  useEffect(() => {
    if (!code || codeExchanged.current) return;
    codeExchanged.current = true;

    supabase.auth.exchangeCodeForSession(window.location.href).then(() => {
      // Clean up the code param from URL without navigating
      const newParams = new URLSearchParams(params.toString());
      newParams.delete("code");
      setParams(newParams, { replace: true });
    }).catch((err: any) => {
      // detectSessionInUrl may have already exchanged it — ignore duplicate errors
      console.warn("Code exchange (may be duplicate):", err);
      const newParams = new URLSearchParams(params.toString());
      newParams.delete("code");
      setParams(newParams, { replace: true });
    });
  }, [code, params, setParams]);

  useEffect(() => {
    // If no token, show error
    if (!token) {
      setStatus("error");
      setMessage("Missing invite token");
      return;
    }

    const run = async () => {
      if (!user) return; // wait for login
      if (hasRun.current) return; // Prevent double execution in StrictMode
      hasRun.current = true;

      try {
        setStatus("working");
        let functionName: string;
        let redirectPath: string;
        let entityType: string;

        if (type === "global") {
          functionName = "admin-invite-consume";
          redirectPath = "/provider/catalog"; // Default to provider catalog for global invites
          entityType = "system";
        } else if (type === "provider") {
          functionName = "provider-invite-consume";
          redirectPath = "/provider/catalog";
          entityType = "provider";
        } else {
          functionName = "shop-invite-consume";
          redirectPath = "/dashboard";
          entityType = "shop";
        }

        const { data, error } = await supabase.functions.invoke(functionName, {
          body: { token },
        });
        if (error) throw error;
        if ((data as any)?.success) {
          setStatus("done");
          const entitiesGranted = (data as any)?.entities_granted;
          if (type === "global" && entitiesGranted !== undefined) {
            setMessage(`Access granted to ${entitiesGranted} entities. Redirecting...`);
          } else {
            setMessage(`Invite accepted. Redirecting to ${entityType}...`);
          }

          // Refresh provider context so routing knows the user is now a provider
          if (type === "provider" || type === "global") {
            const providerId = (data as any)?.provider_id;
            await refreshProviders();
            if (providerId) setCurrentProviderId(providerId);
          }

          setTimeout(() => navigate(redirectPath), 1500);
        } else {
          throw new Error(((data as any)?.error) || "Unknown error");
        }
      } catch (e: any) {
        setStatus("error");
        setMessage(e?.message || "Failed to accept invite");
      }
    };

    run();
  }, [token, type, user, navigate]);

  const handleLogin = async () => {
    if (!token) return;
    // Kick off Google OAuth with redirect back to this page including token and type
    try {
      const redirectUrl = new URL(`${window.location.origin}/accept-invite`);
      redirectUrl.searchParams.set("token", token);
      if (type) redirectUrl.searchParams.set("type", type);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl.toString(),
        },
      });
      if (error) throw error;
    } catch (e) {
      setStatus("error");
      setMessage((e as any)?.message || "Failed to start login");
    }
  };

  const renderContent = () => {
    if (!token) {
      return (
        <div className="text-center">
          <p className="text-red-400 ">Invalid invite link</p>
        </div>
      );
    }

    if (!user) {
      // If we have a code param, we're in the middle of OAuth exchange — show loading
      if (code) {
        return (
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded animate-spin mx-auto mb-4"></div>
            <p className="text-white/50 ">Completing sign-in...</p>
          </div>
        );
      }
      const entityType = type === "provider" ? "provider" : "shop";
      return (
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-3 ">Accept Invite</h2>
          <p className="text-white/50 mb-6 ">Sign in to accept this invite and access the {entityType}.</p>
          <button
            onClick={handleLogin}
            className="px-6 py-3 bg-orange-500 text-white border-2 border-orange-500 hover:bg-orange-400 rounded-xl transition-colors"
          >
            Continue with Google
          </button>
        </div>
      );
    }

    if (status === "working") {
      return (
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded animate-spin mx-auto mb-4"></div>
          <p className="text-white/50 ">Accepting invite...</p>
        </div>
      );
    }

    if (status === "done") {
      return <div className="text-center text-emerald-400 ">{message}</div>;
    }

    if (status === "error") {
      return (
        <div className="text-center">
          <p className="text-red-400 ">{message || "Something went wrong"}</p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 px-4 py-2 border border-white/15  ">Go to Dashboard</button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="glass-card rounded-xl p-6  max-w-md w-full">
        {renderContent()}
      </div>
    </div>
  );
};

export default AcceptInvite;
